<script setup>
import { computed, getCurrentInstance, onMounted, reactive, ref, watch } from "vue";
import utils from "@/utils/utils";
import EventCenter from "@/utils/EventCenter";
import AirwallexDialog from "./AirwallexDialog.vue";

const { proxy } = getCurrentInstance();

const props = defineProps({
  /** 充值弹窗显隐（:visible.sync 使用） */
  visible: {
    type: Boolean,
    default: false,
  },
  /** 当前团队可用余额（右侧明细展示用，由父组件传入） */
  balance: {
    type: Number,
    default: 0,
  },
  /** 默认充值金额（打开弹窗时预填，如支付页传入应付金额） */
  defaultAmount: {
    type: Number,
    default: 0,
  },
});

const emit = defineEmits(["update:visible", "success"]);

const dialogVisible = computed({
  get: () => props.visible,
  set: (val) => emit("update:visible", val),
});

const numberReg = /^\d+(\.\d{1,2})?$/;

function validateNumber(i18n) {
  return (rule, value, callback) => {
    if (!numberReg.test(value)) {
      callback(new Error(i18n("team.Invalid-Amount")));
    } else if (Number(value) <= 0) {
      callback(new Error(i18n("team.Amount-Greater-Zero")));
    } else {
      callback();
    }
  };
}

/** 充值表单数据 */
const formData = reactive({
  currency: "USD",
  recharge: "",
  recharge_channel: "airwallex",
});

// 打开弹窗时预填默认充值金额
watch(
  () => props.visible,
  (val) => {
    if (val) {
      formData.recharge = props.defaultAmount ? props.defaultAmount.toFixed(2) : "";
    }
  }
);

const formRules = {
  currency: { required: true, message: proxy.$t("team.Select-Currency"), trigger: "change" },
  recharge: [
    { required: true, message: proxy.$t("team.Enter-Recharge-Amount"), trigger: "blur" },
    { validator: validateNumber((key) => proxy.$t(key)), trigger: "blur" },
  ],
};

/** 充值渠道列表 */
const rechargeChannels = [
  {
    label: "Airwallex",
    recharge_rates: 0.001,
    value: "airwallex",
    disabled: false,
    icon: `${utils.imageRoot}/team/airwallex.png`,
  },
  {
    label: "LianLian",
    value: "Lian",
    disabled: true,
    recharge_rates: 0.03,
    icon: `${utils.imageRoot}/team/LianLian.png`,
  },
  {
    label: "WLH",
    value: "WLH",
    disabled: true,
    recharge_rates: 0.03,
    icon: `${utils.imageRoot}/team/WLH.png`,
  },
  {
    label: "PingPong",
    value: "pingPong",
    disabled: true,
    recharge_rates: 0.03,
    icon: `${utils.imageRoot}/team/pingpong.png`,
  },
];

const RefDialogRechargeForm = ref(null);
const currencySelect = ref(null);
const currencySelectOpened = ref(false);
const loading = ref(false);

/** 空中云汇支付弹窗 */
const airwallexDialogVisible = ref(false);
const airwallexData = reactive({
  client_secret: "",
  currency: "",
  intent_id: "",
});

/**
 * 充值广播
 * @type {BroadcastChannel|null}
 */
let rechargeBroadChannel = null;

/**
 * 在线充值当前费率
 */
const currenteRechargeRate = computed(() => {
  return rechargeChannels.find((item) => item.value === formData.recharge_channel)?.recharge_rates * 100;
});

/**
 * 当前充值金额
 */
const currentRechargeNumber = computed(() => {
  if (numberReg.test(formData.recharge)) {
    return utils.toThousandsNew(formData.recharge, 2);
  } else {
    return "0.00";
  }
});

/**
 * 当前费率金额
 */
const currentRechargeRateNumber = computed(() => {
  const recharge = formData.recharge;
  if (!numberReg.test(recharge)) return "0.00";
  const channel = rechargeChannels.find((item) => item.value === formData.recharge_channel);
  if (!channel) return "0.00";
  const rateAmount = Number(recharge) * channel.recharge_rates;
  return utils.toThousandsNew(utils.roundHalfEven(rateAmount, 2), 2);
});

/**
 * 当前实付金额（充值金额 + 费率金额）
 */
const currentRecharteActualAmount = computed(() => {
  const recharge = formData.recharge;
  if (!numberReg.test(recharge)) return "0.00";
  const channel = rechargeChannels.find((item) => item.value === formData.recharge_channel);
  if (!channel) return "0.00";
  const actualAmount = Number(recharge) * (1 + channel.recharge_rates);
  return utils.toThousandsNew(utils.roundHalfEven(actualAmount, 2), 2);
});

onMounted(() => {
  rechargeBroadChannel = new BroadcastChannel(EventCenter.RechargeMessage);
});

/**
 * 空中云汇弹窗关闭：清空 intent 数据
 */
function onAirwallexClose() {
  airwallexData.client_secret = "";
  airwallexData.currency = "";
  airwallexData.intent_id = "";
}

/** 空中云汇支付失败回调 */
function onAirwallexFailed(err) {
  console.error("onAirwallexFailed :>>>", err);
  proxy.$message.error(err?.message || proxy.$t("team.Payment-Component-Failed"));
}

/**
 * 关闭所有充值弹窗
 */
function closeAllDialogs() {
  airwallexDialogVisible.value = false;
  dialogVisible.value = false;
}

/** 空中云汇支付成功回调 */
function onAirwallexSuccess(data) {
  console.debug("onAirwallexSuccess :>>>", data);
  proxy.$axios.get_payment_intent(airwallexData.intent_id).then((res) => {
    if (res.code === 1) {
      const status = res.data.status;
      switch (status) {
        case 5: // SUCCEEDED
          proxy.$message.success(proxy.$t("team.Recharge-Success-Short"));
          closeAllDialogs();
          emit("success", formData.recharge);
          // 向订阅了 EventCenter.RechargeMessage 事件的窗口发送充值成功的消息
          rechargeBroadChannel.postMessage("SUCCESS");
          break;

        case 4: // PENDING
          proxy.$message.info(proxy.$t("team.Recharge-Processing"));
          closeAllDialogs();
          emit("success", formData.recharge);
          rechargeBroadChannel.postMessage("SUCCESS");
          break;

        case 3: // REQUIRES_CAPTURE
          proxy.$message.info(proxy.$t("team.Recharge-Accepted"));
          closeAllDialogs();
          emit("success", formData.recharge);
          rechargeBroadChannel.postMessage("SUCCESS");
          break;

        case 2: // REQUIRES_CUSTOMER_ACTION
          proxy.$message.warning(proxy.$t("team.Extra-Verification"));
          // 不关闭弹窗，保持 DropIn 可见以让用户完成验证操作
          break;

        case 1: // REQUIRES_PAYMENT_METHOD
          proxy.$message.error(proxy.$t("team.Payment-Session-Error"));
          closeAllDialogs();
          break;

        case 6: // CANCELLED
          proxy.$message.error(proxy.$t("team.Recharge-Cancelled"));
          closeAllDialogs();
          break;

        default:
          proxy.$message.error(proxy.$t("team.Recharge-Status-Unknown"));
          closeAllDialogs();
      }
    } else {
      proxy.$message.error(res.msg || proxy.$t("team.Query-Status-Failed"));
    }
  });
}

/**
 * 开始充值
 */
async function dialogRechargeConfirm() {
  try {
    loading.value = true;
    await RefDialogRechargeForm.value.validate();

    const res = await proxy.$axios.create_payment(formData.recharge);
    if (res.code === 1) {
      airwallexData.client_secret = res.data.client_secret;
      airwallexData.currency = res.data.currency;
      airwallexData.intent_id = res.data.intent_id;
      airwallexDialogVisible.value = true;
    }
  } finally {
    loading.value = false;
  }
}

/**
 * 充值弹窗关闭：重置表单
 */
function dialogRechargeClose() {
  formData.recharge = "";
  proxy.$nextTick(() => {
    RefDialogRechargeForm.value.clearValidate();
  });
}
</script>

<template>
  <div class="recharge-dialog">
    <el-dialog
      :visible.sync="dialogVisible"
      width="760px"
      custom-class="dialog-recharge"
      :close-on-press-escape="false"
      :close-on-click-modal="false"
      :show-close="false"
      @close="dialogRechargeClose"
    >
      <div class="dialog-recharge__content">
        <div class="dialog-recharge__content__left">
          <p class="left-title">{{ proxy.$t("team.Online-Recharge") }}</p>
          <el-form
            class="recharge-form"
            :model="formData"
            label-position="top"
            ref="RefDialogRechargeForm"
            :rules="formRules"
          >
            <el-form-item prop="currency">
              <template #label>
                <span class="text-0 form-item-label">{{ proxy.$t("team.Currency-Select") }}</span>
              </template>
              <div
                class="currency-select-wrapper"
                :class="{ 'is-opened': currencySelectOpened }"
                @click="currencySelect.focus()"
              >
                <el-select
                  ref="currencySelect"
                  v-model="formData.currency"
                  class="currency-select-hidden"
                  @visible-change="currencySelectOpened = $event"
                >
                  <el-option value="USD">
                    <p class="currency-option__label">
                      <LocalSvgIcon name="USA" :inheritColor="false" class="currency-option__icon" />
                      {{ proxy.$t("team.USD-Label") }}
                    </p>
                  </el-option>
                </el-select>
                <div class="currency-select-display">
                  <LocalSvgIcon name="USA" :inheritColor="false" class="currency-option__icon" />
                  <span>{{ proxy.$t("team.USD-Label") }}</span>
                  <i class="el-icon-arrow-down el-select__caret"></i>
                </div>
              </div>
            </el-form-item>
            <el-form-item prop="recharge">
              <template #label>
                <span class="text-0 form-item-label">{{ proxy.$t("team.Recharge-Amount") }}</span>
              </template>
              <el-input v-model="formData.recharge" :placeholder="proxy.$t('team.Enter-Recharge-Amount')">
                <template #append>
                  <span class="recharge-form__append">$</span>
                </template>
              </el-input>
            </el-form-item>
            <el-form-item>
              <template #label>
                <span class="text-0 form-item-label">{{ proxy.$t("team.Recharge-Channel") }}</span>
              </template>
              <div class="grid-container">
                <div
                  class="card-item"
                  :class="{
                    active: item.value === formData.recharge_channel,
                    disabled: item.disabled,
                  }"
                  v-for="item in rechargeChannels"
                  :key="item.value"
                >
                  <div class="icon-wrapper">
                    <img class="card-item__img" :src="item.icon" alt="" />
                  </div>
                  <div class="card-text">{{ item.label }}</div>
                </div>
              </div>
            </el-form-item>
          </el-form>
          <div class="confirm-button">
            <el-button :loading="loading" type="primary" class="jc-button" @click="dialogRechargeConfirm">{{
              proxy.$t("team.Confirm-Recharge")
            }}</el-button>
          </div>
        </div>
        <div class="dialog-recharge__content__right">
          <p class="right-title">
            <span>{{ proxy.$t("team.Amount-Details") }}</span>
            <LocalSvgIcon name="Close" class="icon-close" @click="dialogVisible = false" />
          </p>
          <div class="balance-available-box">
            <p class="text-2">{{ proxy.$t("team.Current-Team-Balance") }}</p>
            <p class="text-0 balance-available-box__amount">$ {{ utils.toThousandsNew(props.balance, 2) }}</p>
            <div class="bg-layer"></div>
          </div>

          <div class="recharge-info">
            <p class="text-2">{{ proxy.$t("team.Recharge-Amount") }}</p>
            <p class="text-0 recharge-info__amount">$ {{ currentRechargeNumber }}</p>
          </div>
          <div class="recharge-info">
            <p class="text-2">{{ proxy.$t("team.Recharge-Rate") }}</p>
            <p class="text-0 recharge-info__amount">{{ currenteRechargeRate }}%</p>
          </div>
          <div class="recharge-info">
            <p class="text-2">{{ proxy.$t("team.Recharge-Rate-Amount") }}</p>
            <p class="text-0 recharge-info__amount">$ {{ currentRechargeRateNumber }}</p>
          </div>
          <!-- 分割线 -->
          <div class="divider-line"></div>

          <p class="text-2 recharge-info__total-label">{{ proxy.$t("team.Actual-Pay-Amount") }}</p>
          <p class="text-0 recharge-info__total-amount">$ {{ currentRecharteActualAmount }}</p>
        </div>
      </div>
    </el-dialog>

    <AirwallexDialog
      :visible.sync="airwallexDialogVisible"
      :data="airwallexData"
      @success="onAirwallexSuccess"
      @error="onAirwallexFailed"
      @close="onAirwallexClose"
    />
  </div>
</template>

<style lang="scss" scoped>
.dialog-recharge__content {
  display: flex;

  &__left {
    padding: 24px;
    box-sizing: border-box;
    flex-basis: 460px;

    .left-title {
      font-size: 18px;
      font-weight: 600;
      color: var(--mc-primary-text-color-v2);
      line-height: 26px;
      margin-bottom: 24px;
    }

    /* --- 核心 Grid 布局 --- */
    .grid-container {
      display: grid;
      /* 创建2列，每列平均分配剩余空间 (1fr) */
      grid-template-columns: repeat(2, 1fr);
      /* 设置间距：行间距 8px，列间距 8px */
      gap: 8px;

      /* 模拟图中容器的宽度限制 (让选中态看起来比例协调) */
      max-width: 450px;
      margin: 0 auto;
    }

    /* --- 单个卡片样式 --- */
    .card-item {
      display: flex;
      align-items: center;
      padding: 0 16px;
      height: 56px;
      background: #fafafa;
      border: 1px solid var(--mc-border-color-v2);
      border-radius: var(--mc-border-radius-normal);
      box-shadow: 0 1px 2px rgba(0, 0, 0, 0.02);
      transition: all 0.2s;
      cursor: pointer;

      &.active {
        border: 1px solid var(--mc-color-primary);
        position: relative;
        background-color: var(--mc-color-primary-bg);
      }

      &.disabled {
        cursor: not-allowed;
        user-select: none;
      }
    }

    /* --- 内部元素样式 (图标 + 文字) --- */
    .icon-wrapper {
      width: 32px;
      height: 32px;
      border-radius: 4px;
      display: flex;
      justify-content: center;
      align-items: center;
      margin-right: 12px;
      color: white;
      font-size: 12px;
      font-weight: bold;
    }

    .card-item__img {
      width: 100%;
      height: 100%;
    }

    .card-text {
      color: var(--mc-primary-text-color-v2);
      font-size: 14px;
      font-weight: 500;
    }

    .confirm-button {
      display: flex;
      margin-top: 24px;

      .jc-button {
        flex: 1;
        justify-content: center;
        .el-icon-loading {
          line-height: 20px;
        }
      }
    }

    .currency-option__label {
      display: flex;
      align-items: center;
      gap: 8px;
    }

    .currency-option__icon {
      width: 22px;
      height: 16px;
    }

    .currency-select-wrapper {
      position: relative;
      cursor: pointer;

      .currency-select-hidden {
        position: absolute;
        width: 100%;
        z-index: 1;
        opacity: 0;

        :deep(.el-input__inner) {
          cursor: pointer;
        }
      }

      .currency-select-display {
        display: flex;
        align-items: center;
        gap: 8px;
        height: 36px;
        padding: 0 15px;
        border: 1px solid #dcdfe6;
        border-radius: var(--mc-border-radius-normal);
        background: var(--mc-background-main-color);
        color: #606266;
        font-size: 14px;
        pointer-events: none;

        .el-select__caret {
          margin-left: auto;
          color: #c0c4cc;
          font-size: 14px;
          transition: transform 0.3s;
        }
      }

      &.is-opened .currency-select-display .el-select__caret {
        transform: rotateZ(180deg);
      }
    }

    .recharge-form {
      .el-form-item__label {
        padding-bottom: 8px;
      }

      div.el-form-item--small.el-form-item {
        margin-bottom: 16px;
      }

      span.form-item-label {
        font-size: 14px;
        line-height: 22px;
        font-weight: 400;
      }
    }

    .recharge-form__append {
      color: #727a84;
      font-size: 14px;
      line-height: 22px;
    }
  }

  &__right {
    background-color: #fafafa;
    flex-basis: 300px;
    padding: 24px;
    box-sizing: border-box;

    .right-title {
      color: var(--mc-primary-text-color-v2);
      font-size: 18px;
      font-weight: 400;
      line-height: 26px;
      margin-bottom: 24px;
      display: flex;
      align-items: center;
      justify-content: space-between;

      .icon-close {
        width: 24px;
        height: 24px;
        cursor: pointer;
      }
    }

    .balance-available-box {
      position: relative;
      background-color: #faf2e0;
      border: 1px solid #fae9c8;
      padding: 16px;
      border-radius: var(--mc-border-radius-normal);
      margin-bottom: 24px;

      &__amount {
        font-weight: 500;
        font-size: 14px;
        line-height: 22px;
        margin-top: 4px;
      }

      .bg-layer {
        background-image: url("#{$image-root}/team/bill.png");
        background-size: cover;
        background-repeat: no-repeat;
        width: 64px;
        height: 64px;
        position: absolute;
        right: 0;
        bottom: 0;
      }
    }

    .recharge-info {
      display: flex;
      justify-content: space-between;
      font-size: 14px;
      line-height: 22px;

      &__amount {
        font-weight: 500;
      }

      &__total-label {
        line-height: 22px;
      }

      &__total-amount {
        line-height: 32px;
        font-size: 24px;
        font-weight: 500;
      }
    }

    .recharge-info + .recharge-info {
      margin-top: 8px;
    }
  }
}

.divider-line {
  width: 100%;
  height: 1px;
  background-color: #e0e5e8;
  margin: 24px 0;
}

.text-0 {
  color: var(--mc-primary-text-color-v2);
  font-weight: 600;
}

.text-2 {
  color: var(--mc-desc-text-color);
  font-size: 12px;
  line-height: 20px;
}
</style>

<style lang="scss">
.dialog-recharge {
  border-radius: 12px;
  border: 1px solid var(--mc-border-color-v2);
  overflow: hidden;

  .el-dialog__header {
    display: none;
  }

  .el-dialog__body {
    padding: 0;
  }
}
</style>
